/**
 * Layout PropTypes
 *
 * PropType definitions for layout components including breadcrumbs, SEO, and navigation structures.
 * Used in page layouts, routing, and metadata management.
 *
 * @module propTypes/layout
 */

import PropTypes from "prop-types";

/**
 * Breadcrumb page shape
 * Represents a single breadcrumb navigation item
 *
 * @property {string} label - Display label for breadcrumb
 * @property {string} path - URL path for breadcrumb link
 */
export const breadcrumbPageShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
});

/**
 * SEO meta image shape
 * Represents an image for Open Graph and social media metadata
 *
 * @property {string} url - Image URL
 */
export const seoMetaImageShape = PropTypes.shape({
  url: PropTypes.string,
});

/**
 * SEO Open Graph shape
 * Open Graph metadata for social sharing
 *
 * @property {string} title - OG title
 * @property {string} description - OG description
 * @property {string} type - OG type (website, article, etc.)
 * @property {string} url - OG URL
 * @property {Object} image - OG image
 */
export const seoOpenGraphShape = PropTypes.shape({
  title: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  image: seoMetaImageShape,
});

/**
 * SEO data shape
 * Complete SEO metadata for a page
 *
 * @property {string} metaTitle - Page title
 * @property {string} metaDescription - Meta description
 * @property {string} keywords - Meta keywords
 * @property {string} canonicalURL - Canonical URL
 * @property {string} metaRobots - Robots meta tag
 * @property {string} metaViewport - Viewport meta tag
 * @property {Object} metaImage - Meta image
 * @property {Array} openGraph - Open Graph data array
 * @property {Object|string} structuredData - JSON-LD structured data
 */
export const seoDataShape = PropTypes.shape({
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  keywords: PropTypes.string,
  canonicalURL: PropTypes.string,
  metaRobots: PropTypes.string,
  metaViewport: PropTypes.string,
  metaImage: seoMetaImageShape,
  openGraph: PropTypes.arrayOf(seoOpenGraphShape),
  structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
});

/**
 * Swiper/slider options shape
 * Configuration for Swiper carousel components
 *
 * @property {boolean} navigation - Enable navigation arrows
 * @property {boolean} pagination - Enable pagination dots
 * @property {boolean|Object} autoplay - Enable autoplay
 * @property {number} slidesPerView - Number of slides visible
 * @property {number} spaceBetween - Space between slides
 * @property {boolean} loop - Enable loop mode
 * @property {Array} modules - Swiper modules array
 */
export const swiperOptionsShape = PropTypes.shape({
  navigation: PropTypes.bool,
  pagination: PropTypes.bool,
  autoplay: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  slidesPerView: PropTypes.number,
  spaceBetween: PropTypes.number,
  loop: PropTypes.bool,
  modules: PropTypes.array,
});
